import React, { useState, useEffect } from "react";
import { useLogin } from "../hooks/useLogin";
import { useSignup } from "../hooks/useSignup";
import { useResetPassword } from "../hooks/useResetPassword";
import "../styles.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FaGoogle } from "react-icons/fa";
import { FiMail, FiLock, FiArrowLeft, FiCheck, FiZap } from "react-icons/fi";
import { useAuthContext } from '../hooks/useAuthContext';
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import dayjs from 'dayjs';
import { jwtDecode } from "jwt-decode";
import { Helmet } from "react-helmet";

const Login = () => {
    const { user, dispatch } = useAuthContext()
    const [searchParams] = useSearchParams()
    const oauthError = searchParams.get('error')
    const next = searchParams.get('next')

    const [btnState, changeBtnState] = useState(true);   // true = Login, false = Signup
    const [dspState, changeDspState] = useState("")       // "" = auth view
    const [rstState, changeResetState] = useState("none") // "" = reset view

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login, err } = useLogin();
    const { signup, error, success } = useSignup();
    const { resetPassword, rstErr, succ } = useResetPassword()

    const showReset = rstState === ""
    const showAuth = dspState !== "none"

    function rstPass() { changeDspState("none"); changeResetState("") }
    function backtologin() { changeResetState("none"); changeDspState("") }
    const goLogin = () => changeBtnState(true)
    const goSignup = () => changeBtnState(false)

    const handleLogin = async (e) => { e.preventDefault(); await login(email, password) }
    const handleSignup = async (e) => { e.preventDefault(); await signup(email, password) }
    const handleReset = async (e) => { e.preventDefault(); await resetPassword(email) }

    const loginWithGoogle = () => {
        // Carry ?next through Google OAuth so e.g. AppSumo users starting at /redeem
        // come back to /redeem after signing in with Gmail, not the dashboard.
        const q = next && next.startsWith('/') ? `?next=${encodeURIComponent(next)}` : ''
        window.open(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/auth/google${q}`, "_self")
    }

    useEffect(() => {
        const checkRT = async () => {
            try {
                const refresh_token = Cookies.get('refresh_token')
                if (!user) {
                    const refreshToken = jwtDecode(refresh_token)
                    const isExpired = dayjs.unix(refreshToken.exp).diff(dayjs()) < 1;
                    if (!isExpired || refreshToken !== undefined || refresh_token !== null) {
                        const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/user/refresh-token`, {
                            method: 'GET',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include'
                        })
                        if (response.status === 200) {
                            const json = await response.json()
                            dispatch({ type: 'LOGIN', payload: json.token })
                        } else {
                            Cookies.remove("token")
                            Cookies.remove("refresh_token")
                            dispatch({ type: 'LOGOUT' })
                        }
                    } else {
                        Cookies.remove("refresh_token")
                    }
                } else if (!user || !refresh_token || refresh_token === undefined) {
                    Cookies.remove("refresh_token")
                    Cookies.remove("token")
                    dispatch({ type: 'LOGOUT' })
                }
            } catch (e) {
                Cookies.remove("refresh_token")
                Cookies.remove("token")
                dispatch({ type: 'LOGOUT' })
            }
        }
        checkRT()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const inputCls = "w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 transition"
    const primaryBtn = "w-full py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition disabled:opacity-60"

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-Rajdhani bg-white">
            <Helmet>
                <meta charSet="utf-8" />
                <title>Login - Content Flash AI</title>
                <meta name="description" content="Build AI-powered forms in seconds with Content Flash AI." />
            </Helmet>

            {/* ── Left: marketing ─────────────────────────────────────────── */}
            <div className="relative hidden lg:flex flex-col justify-between p-12 xl:p-16 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-violet-50 border-r border-gray-100">
                <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl" />
                <div className="pointer-events-none absolute -bottom-28 -left-16 w-96 h-96 bg-violet-200/40 rounded-full blur-3xl" />

                <img src="/assets/Flash-logo-light.png" alt="Content Flash AI" className="relative h-9 w-auto object-contain" />

                <div className="relative max-w-lg">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white text-indigo-600 border border-indigo-100 shadow-sm">
                        <FiZap className="w-3.5 h-3.5" /> AI Form Builder
                    </span>
                    <h1 className="mt-5 text-4xl xl:text-[2.75rem] font-bold leading-[1.1] text-gray-900">
                        Describe it.{" "}
                        <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">AI builds your form.</span>
                    </h1>
                    <p className="mt-4 text-lg text-gray-500">
                        Go from a one-line prompt to a polished, conversational form — fields, logic, themes and integrations, all done for you.
                    </p>

                    {/* Light product mock */}
                    <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-4 shadow-lg shadow-indigo-100/60">
                        <div className="flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm text-gray-600">
                            <FiZap className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                            <span className="truncate">"Create a customer feedback form with a rating"</span>
                        </div>
                        <div className="mt-3 space-y-2.5">
                            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400">Full name</p>
                                <div className="mt-1 h-2 w-2/3 rounded bg-gray-200" />
                            </div>
                            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400">How likely are you to recommend us?</p>
                                <div className="mt-1.5 text-amber-400 text-sm">★ ★ ★ ★ <span className="text-gray-300">★</span></div>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-600"><FiCheck className="w-3 h-3" /> Generated in 2.3s</span>
                                <span className="text-[11px] text-gray-400">Logic · Themes · Integrations</span>
                            </div>
                        </div>
                    </div>

                    <ul className="mt-8 space-y-2.5">
                        {["Conversational, one-question-at-a-time forms", "Conditional logic & smart validation", "Slack, Sheets, Notion & HubSpot integrations"].map((f) => (
                            <li key={f} className="flex items-center gap-3 text-gray-600">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><FiCheck className="w-3 h-3" /></span>
                                {f}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative flex items-center gap-3 text-sm text-gray-500">
                    <div className="flex -space-x-2">
                        {["bg-pink-400", "bg-amber-400", "bg-emerald-400", "bg-sky-400"].map((c, i) => (
                            <span key={i} className={`w-7 h-7 rounded-full ${c} ring-2 ring-white`} />
                        ))}
                    </div>
                    Trusted by 1,000+ teams building smarter forms
                </div>
            </div>

            {/* ── Right: auth ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-center p-6 sm:p-10 bg-gray-50 lg:bg-white">
                <div className="w-full max-w-sm">
                    {/* Mobile logo */}
                    <div className="lg:hidden text-center mb-7">
                        <img src="/assets/Flash-logo-light.png" alt="Content Flash AI" className="h-9 w-auto object-contain mx-auto" />
                    </div>

                    {showReset ? (
                        <form onSubmit={handleReset} className="space-y-5">
                            <button type="button" onClick={backtologin} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-indigo-600 transition">
                                <FiArrowLeft className="w-4 h-4" /> Back to login
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Reset your password</h1>
                                <p className="text-sm text-gray-500 mt-1">We'll email you instructions to reset it.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                <div className="relative">
                                    <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} />
                                </div>
                            </div>
                            <button className={primaryBtn}>Send reset instructions</button>
                            {rstErr && <p className="text-sm text-center text-red-600 font-medium">{rstErr}</p>}
                            {succ && <p className="text-sm text-center text-green-600 font-medium">{succ}</p>}
                        </form>
                    ) : showAuth && (
                        <div className="space-y-5">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {btnState ? "Welcome back" : "Start building for free"}
                                </h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    {btnState ? "Log in to continue building forms." : "Create your first AI form in under a minute."}
                                </p>
                            </div>

                            {/* Tabs */}
                            <div className="grid grid-cols-2 p-1 bg-gray-100 rounded-lg text-sm font-medium">
                                <button type="button" onClick={goLogin} className={`py-2 rounded-md transition ${btnState ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Log in</button>
                                <button type="button" onClick={goSignup} className={`py-2 rounded-md transition ${!btnState ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Sign up</button>
                            </div>

                            <button onClick={loginWithGoogle} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                                <FaGoogle className="text-[#EA4335]" size={15} /> Continue with Google
                            </button>
                            {oauthError === 'account_exists' && <p className="text-sm text-center text-red-600 font-medium">An account with this email already exists. Please log in with your email and password.</p>}

                            <div className="flex items-center gap-3">
                                <hr className="flex-grow border-gray-200" />
                                <span className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">or</span>
                                <hr className="flex-grow border-gray-200" />
                            </div>

                            <form onSubmit={btnState ? handleLogin : handleSignup} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                    <div className="relative">
                                        <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="block text-sm font-medium text-gray-700">Password</label>
                                        {btnState && (
                                            <button type="button" onClick={rstPass} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">Forgot password?</button>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
                                    </div>
                                </div>

                                <button className={primaryBtn}>{btnState ? "Log in" : "Create free account"}</button>

                                {btnState && err && <div className="text-sm text-center text-red-600 font-medium">{err}</div>}
                                {!btnState && error && <div className="text-sm text-center text-red-600 font-medium">{error}</div>}
                                {!btnState && success && <div className="text-sm text-center text-green-600 font-medium">{success}</div>}
                            </form>

                            {/* Signup perks — nudge curiosity */}
                            {!btnState && (
                                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                    <span className="inline-flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> Free forever plan</span>
                                    <span className="inline-flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> No credit card</span>
                                    <span className="inline-flex items-center gap-1"><FiCheck className="w-3 h-3 text-emerald-500" /> 1-minute setup</span>
                                </div>
                            )}

                            <p className="text-center text-sm text-gray-500">
                                {btnState ? (
                                    <>New here? <button onClick={goSignup} className="font-semibold text-indigo-600 hover:text-indigo-700">Create a free account</button></>
                                ) : (
                                    <>Already have an account? <button onClick={goLogin} className="font-semibold text-indigo-600 hover:text-indigo-700">Log in</button></>
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Login;
